// IR (see mps803.js) -> Unicode text. Characters are placed by dot position
// (6 dots per column); wide characters take two columns. Bit image lines can't
// be shown as text and become a placeholder.
//
// Two views of the same cells:
//   lineToText      plain text (for the .txt download): reverse video has no
//                   plain-text form and is dropped
//   lineToSegments  [{ text, reverse }] runs for the screen, where reverse
//                   video is drawn with inverted colours
import { GRAPHICS, BUSINESS, codeIndex } from '../core/petscii.js';
import { CHAR_WIDTH } from '../core/mps803.js';

const isBlank = (cell) => /^\s$/.test(cell.ch);

function bitCount(line) {
  return line.items.filter((i) => i.kind === 'bits').length;
}

export function lineToText(line) {
  if (line.pageBreak) return '\f'; // forced page end
  // A line can mix both (text after graphics is ignored by the printer, but
  // text before is kept): show the text part, then the placeholder.
  const text = charCells(line.items, false).map((c) => c.ch).join('');
  const bits = bitCount(line);
  return bits > 0 ? `${text}${text ? ' ' : ''}[bit image, ${bits} dots]` : text;
}

// Runs of equal reverse state for the screen. A segment with `note: true` is
// the bit image placeholder. Trailing blanks are dropped unless they are
// reverse (a reversed space is a visible filled cell on the printer).
export function lineToSegments(line) {
  if (line.pageBreak) return [];
  const segments = [];
  for (const cell of charCells(line.items, true)) {
    const last = segments[segments.length - 1];
    if (last && last.reverse === cell.rev) last.text += cell.ch;
    else segments.push({ text: cell.ch, reverse: cell.rev });
  }
  const bits = bitCount(line);
  if (bits > 0) {
    if (segments.length) segments.push({ text: ' ', reverse: false });
    segments.push({ text: `[bit image, ${bits} dots]`, reverse: false, note: true });
  }
  return segments;
}

function charCells(items, keepReverseBlanks) {
  const cols = [];
  for (const it of items) {
    if (it.kind !== 'char') continue;
    const table = it.set === 'business' ? BUSINESS : GRAPHICS;
    const ch = table[codeIndex(it.code)] ?? ' ';
    const col = Math.round(it.x / CHAR_WIDTH);
    const rev = Boolean(it.reverse);
    cols[col] = { ch, rev };
    if (it.wide) cols[col + 1] ??= { ch: ' ', rev };
  }
  const cells = [];
  for (let i = 0; i < cols.length; i++) cells.push(cols[i] ?? { ch: ' ', rev: false });
  while (cells.length && isBlank(cells[cells.length - 1]) && !(keepReverseBlanks && cells[cells.length - 1].rev)) {
    cells.pop();
  }
  return cells;
}

export function linesToText(lines) {
  return lines.map(lineToText).join('\n');
}
